import React, { useState, useEffect, useCallback } from 'react';
import {
  BuildingOfficeIcon,
  ChatBubbleLeftRightIcon,
  CurrencyDollarIcon,
  ClockIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  DocumentTextIcon,
  PencilSquareIcon,
  FunnelIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

const API_BASE = import.meta.env.VITE_ADMIN_API_URL || import.meta.env.VITE_API_URL || '';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getAuthHeaders() {
  const token = localStorage.getItem('adminToken');
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

async function apiFetch(path, options = {}) {
  const res = await fetch(`${API_BASE}${path}`, {
    credentials: 'include',
    ...options,
    headers: { ...getAuthHeaders(), ...options.headers },
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.message || `Request failed (${res.status})`);
  }
  return res.json();
}

function formatCurrency(value) {
  return new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
    minimumFractionDigits: 2,
  }).format(Number(value) || 0);
}

function formatMonth(dateStr) {
  if (!dateStr) return '-';
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-AU', { year: 'numeric', month: 'short' });
}

/** Generate the last 12 months as YYYY-MM-01 options. */
function generateMonthOptions() {
  const options = [];
  const now = new Date();
  for (let i = 0; i < 12; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
    const label = d.toLocaleDateString('en-AU', { year: 'numeric', month: 'long' });
    options.push({ value, label });
  }
  return options;
}

// ---------------------------------------------------------------------------
// Status badge component
// ---------------------------------------------------------------------------

const STATUS_CONFIG = {
  pending: {
    label: 'Pending',
    bg: 'bg-yellow-100',
    text: 'text-yellow-800',
    dot: 'bg-yellow-400',
  },
  invoiced: {
    label: 'Invoiced',
    bg: 'bg-blue-100',
    text: 'text-blue-800',
    dot: 'bg-blue-400',
  },
  paid: {
    label: 'Paid',
    bg: 'bg-green-100',
    text: 'text-green-800',
    dot: 'bg-green-400',
  },
  overdue: {
    label: 'Overdue',
    bg: 'bg-red-100',
    text: 'text-red-800',
    dot: 'bg-red-400',
  },
};

function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.pending;
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium ${cfg.bg} ${cfg.text}`}
    >
      <span className={`h-1.5 w-1.5 rounded-full ${cfg.dot}`} />
      {cfg.label}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Notes Modal
// ---------------------------------------------------------------------------

function NotesModal({ isOpen, onClose, record, onSave }) {
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isOpen && record) {
      setNotes(record.notes || '');
    }
  }, [isOpen, record]);

  if (!isOpen) return null;

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(record.id, { notes });
      onClose();
    } catch {
      // error toast handled by caller
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black/40" onClick={onClose} />
      <div className="relative z-10 w-full max-w-md rounded-xl bg-white p-6 shadow-xl">
        <h3 className="text-lg font-semibold text-gray-900 mb-1">
          Add Notes
        </h3>
        <p className="text-sm text-gray-500 mb-4">{record?.tenantName || 'Tenant'}</p>
        <textarea
          className="input w-full min-h-[120px] resize-y"
          placeholder="Enter billing notes..."
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
        />
        <div className="mt-4 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="btn-secondary"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="inline-flex items-center rounded-lg bg-purple-600 px-4 py-2 text-sm font-medium text-white hover:bg-purple-700 disabled:opacity-50 transition-colors"
          >
            {saving ? 'Saving...' : 'Save Notes'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Summary Card
// ---------------------------------------------------------------------------

function SummaryCard({ icon: Icon, iconBg, iconColor, title, value, subtitle }) {
  return (
    <div className="card p-6">
      <div className="flex items-center justify-between">
        <div className={`p-3 rounded-lg ${iconBg}`}>
          <Icon className={`w-6 h-6 ${iconColor}`} />
        </div>
      </div>
      <div className="mt-4">
        <h3 className="text-2xl font-bold text-gray-900">{value}</h3>
        <p className="text-sm text-gray-500 mt-1">{title}</p>
        {subtitle && (
          <p className="text-xs text-gray-400 mt-0.5">{subtitle}</p>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main Page Component
// ---------------------------------------------------------------------------

const MONTH_OPTIONS = generateMonthOptions();

const SmsBillingPage = () => {
  // ---- state ----
  const [loading, setLoading] = useState(true);
  const [records, setRecords] = useState([]);
  const [summary, setSummary] = useState({
    totalTenants: 0,
    totalSmsSent: 0,
    totalRevenue: 0,
    pendingCount: 0,
    pendingAmount: 0,
  });
  const [tenantOptions, setTenantOptions] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState('');
  const [selectedTenant, setSelectedTenant] = useState('');
  const [updatingId, setUpdatingId] = useState(null);
  const [notesModal, setNotesModal] = useState({ open: false, record: null });
  const [error, setError] = useState(null);

  // ---- data fetching ----

  const fetchBillingData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (selectedMonth) params.set('month', selectedMonth);
      if (selectedTenant) params.set('tenantId', selectedTenant);
      const qs = params.toString();

      const data = await apiFetch(`/api/admin/sms-billing${qs ? `?${qs}` : ''}`);

      // Support multiple response shapes from the API
      const rows = data.data || data.records || data.billingRecords || data.billing || [];
      setRecords(rows);

      // Derive summary from response payload or compute from records
      if (data.summary) {
        setSummary({
          totalTenants: data.summary.totalTenants ?? data.summary.total_tenants ?? 0,
          totalSmsSent: data.summary.totalSmsSent ?? data.summary.total_sms ?? 0,
          totalRevenue: data.summary.totalRevenue ?? data.summary.total_revenue ?? 0,
          pendingCount: data.summary.pendingCount ?? data.summary.pending_count ?? 0,
          pendingAmount: data.summary.pendingAmount ?? data.summary.pending_amount ?? 0,
        });
      } else {
        const totalSmsSent = rows.reduce(
          (sum, r) => sum + (Number(r.smsSent) || Number(r.total_sms_sent) || 0),
          0
        );
        const totalRevenue = rows.reduce(
          (sum, r) => sum + (Number(r.totalCost) || Number(r.total_cost) || 0),
          0
        );
        const pendingRows = rows.filter(
          (r) => (r.billingStatus || r.billing_status) === 'pending'
        );
        const tenantIds = new Set(rows.map((r) => r.tenantId || r.tenant_id));
        setSummary({
          totalTenants: data.totalTenants ?? tenantIds.size,
          totalSmsSent: data.totalSmsSent ?? totalSmsSent,
          totalRevenue: data.totalRevenue ?? totalRevenue,
          pendingCount: data.pendingCount ?? pendingRows.length,
          pendingAmount:
            data.pendingAmount ??
            pendingRows.reduce(
              (sum, r) => sum + (Number(r.totalCost) || Number(r.total_cost) || 0),
              0
            ),
        });
      }

      // Build tenant filter options from returned data
      const tenantsMap = new Map();
      rows.forEach((r) => {
        const id = r.tenantId || r.tenant_id;
        const name = r.tenantName || r.tenant_name || r.tenant_slug;
        if (id && name) {
          tenantsMap.set(id, name);
        }
      });
      setTenantOptions(
        Array.from(tenantsMap.entries())
          .map(([id, name]) => ({ id, name }))
          .sort((a, b) => a.name.localeCompare(b.name))
      );
    } catch (err) {
      setError(err.message || 'Failed to load SMS billing data');
      console.error('SMS billing fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [selectedMonth, selectedTenant]);

  useEffect(() => {
    fetchBillingData();
  }, [fetchBillingData]);

  // ---- actions ----

  const updateBillingStatus = async (id, payload) => {
    setUpdatingId(id);
    try {
      await apiFetch(`/api/admin/sms-billing/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(payload),
      });
      toast.success('Billing record updated');
      await fetchBillingData();
    } catch (err) {
      toast.error(err.message || 'Failed to update billing record');
      console.error('Update error:', err);
    } finally {
      setUpdatingId(null);
    }
  };

  const handleMarkInvoiced = (record) => {
    const invoiceId = window.prompt('Enter Invoice ID (optional):');
    if (invoiceId === null) return; // user cancelled
    updateBillingStatus(record.id, {
      billingStatus: 'invoiced',
      invoiceId: invoiceId || undefined,
    });
  };

  const handleMarkPaid = (record) => {
    updateBillingStatus(record.id, { billingStatus: 'paid' });
  };

  const handleSaveNotes = async (id, payload) => {
    await updateBillingStatus(id, payload);
  };

  // ---- normalise field access across snake_case / camelCase responses ----

  const getField = (row, camel, snake) => row[camel] ?? row[snake] ?? null;
  const getRowStatus = (row) =>
    getField(row, 'billingStatus', 'billing_status') || 'pending';

  // ---- loading state ----
  if (loading && records.length === 0) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">SMS Billing</h1>
          <p className="text-gray-500 mt-1">Manage SMS usage billing across all tenants</p>
        </div>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
        </div>
      </div>
    );
  }

  // ---- error state ----
  if (error && records.length === 0) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">SMS Billing</h1>
          <p className="text-gray-500 mt-1">Manage SMS usage billing across all tenants</p>
        </div>
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start">
          <ExclamationCircleIcon className="w-5 h-5 text-red-600 mt-0.5 mr-3 flex-shrink-0" />
          <div>
            <p className="text-sm text-red-700">{error}</p>
            <button
              onClick={fetchBillingData}
              className="mt-2 text-sm font-medium text-red-700 underline hover:text-red-800"
            >
              Try again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">SMS Billing</h1>
          <p className="text-gray-500 mt-1">Manage SMS usage billing across all tenants</p>
        </div>
        <button
          onClick={fetchBillingData}
          disabled={loading}
          className="btn-secondary inline-flex items-center gap-2"
        >
          <ArrowPathIcon className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <SummaryCard
          icon={BuildingOfficeIcon}
          iconBg="bg-purple-100"
          iconColor="text-purple-600"
          title="Total Tenants Using SMS"
          value={summary.totalTenants.toLocaleString()}
        />
        <SummaryCard
          icon={ChatBubbleLeftRightIcon}
          iconBg="bg-blue-100"
          iconColor="text-blue-600"
          title="Total SMS Sent"
          value={summary.totalSmsSent.toLocaleString()}
          subtitle="All time"
        />
        <SummaryCard
          icon={CurrencyDollarIcon}
          iconBg="bg-green-100"
          iconColor="text-green-600"
          title="Total Revenue"
          value={formatCurrency(summary.totalRevenue)}
          subtitle="All time"
        />
        <SummaryCard
          icon={ClockIcon}
          iconBg="bg-yellow-100"
          iconColor="text-yellow-600"
          title="Pending Invoices"
          value={summary.pendingCount.toLocaleString()}
          subtitle={formatCurrency(summary.pendingAmount)}
        />
      </div>

      {/* Filters */}
      <div className="card p-4">
        <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
          <div className="flex items-center gap-2 text-gray-500">
            <FunnelIcon className="w-5 h-5" />
            <span className="text-sm font-medium">Filters</span>
          </div>

          <select
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value)}
            className="input"
          >
            <option value="">All Months</option>
            {MONTH_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>

          <select
            value={selectedTenant}
            onChange={(e) => setSelectedTenant(e.target.value)}
            className="input"
          >
            <option value="">All Tenants</option>
            {tenantOptions.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Billing Table */}
      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="table-header">Tenant Name</th>
                <th className="table-header">Month</th>
                <th className="table-header text-right">SMS Sent</th>
                <th className="table-header text-right">Segments</th>
                <th className="table-header text-right">Total Cost ($)</th>
                <th className="table-header">Status</th>
                <th className="table-header">Invoice ID</th>
                <th className="table-header text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan={8} className="text-center py-12">
                    <div className="inline-flex items-center gap-2 text-gray-500">
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-purple-600" />
                      <span>Loading billing records...</span>
                    </div>
                  </td>
                </tr>
              ) : records.length === 0 ? (
                <tr>
                  <td colSpan={8} className="text-center py-16">
                    <ChatBubbleLeftRightIcon className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-sm font-medium text-gray-500">
                      No SMS billing records found
                    </p>
                    <p className="text-xs text-gray-400 mt-1">
                      {selectedMonth || selectedTenant
                        ? 'Try adjusting your filters'
                        : 'Billing records will appear once tenants start sending SMS'}
                    </p>
                  </td>
                </tr>
              ) : (
                records.map((record) => {
                  const status = getRowStatus(record);
                  const isUpdating = updatingId === record.id;
                  const tenantName =
                    getField(record, 'tenantName', 'tenant_name') ||
                    getField(record, 'tenantSlug', 'tenant_slug') ||
                    'Unknown Tenant';
                  const month =
                    getField(record, 'month', 'billing_month');
                  const smsSent =
                    Number(getField(record, 'smsSent', 'total_sms_sent')) || 0;
                  const segments =
                    Number(getField(record, 'segments', 'total_segments')) || 0;
                  const totalCost =
                    Number(getField(record, 'totalCost', 'total_cost')) || 0;
                  const invoiceId =
                    getField(record, 'invoiceId', 'invoice_id');

                  return (
                    <tr key={record.id} className="hover:bg-gray-50">
                      {/* Tenant Name */}
                      <td className="table-cell">
                        <div className="flex items-center">
                          <div className="p-2 bg-purple-50 rounded-lg mr-3">
                            <BuildingOfficeIcon className="w-4 h-4 text-purple-600" />
                          </div>
                          <span className="font-medium text-gray-900">
                            {tenantName}
                          </span>
                        </div>
                      </td>

                      {/* Month */}
                      <td className="table-cell text-gray-600">
                        {formatMonth(month)}
                      </td>

                      {/* SMS Sent */}
                      <td className="table-cell text-right font-medium text-gray-900">
                        {smsSent.toLocaleString()}
                      </td>

                      {/* Segments */}
                      <td className="table-cell text-right text-gray-600">
                        {segments.toLocaleString()}
                      </td>

                      {/* Total Cost */}
                      <td className="table-cell text-right font-medium text-gray-900">
                        {formatCurrency(totalCost)}
                      </td>

                      {/* Status */}
                      <td className="table-cell">
                        <StatusBadge status={status} />
                      </td>

                      {/* Invoice ID */}
                      <td className="table-cell text-sm">
                        {invoiceId ? (
                          <span className="font-mono text-xs bg-gray-100 text-gray-700 rounded px-2 py-1">
                            {invoiceId}
                          </span>
                        ) : (
                          <span className="text-gray-400">&mdash;</span>
                        )}
                      </td>

                      {/* Actions */}
                      <td className="table-cell text-right">
                        <div className="flex items-center justify-end gap-1">
                          {/* Mark as Invoiced */}
                          {status === 'pending' && (
                            <button
                              onClick={() => handleMarkInvoiced(record)}
                              disabled={isUpdating}
                              className="inline-flex items-center gap-1 rounded-lg px-2.5 py-1.5 text-xs font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 disabled:opacity-50 transition-colors"
                              title="Mark as Invoiced"
                            >
                              <DocumentTextIcon className="w-3.5 h-3.5" />
                              Invoice
                            </button>
                          )}

                          {/* Mark as Paid */}
                          {(status === 'invoiced' || status === 'overdue') && (
                            <button
                              onClick={() => handleMarkPaid(record)}
                              disabled={isUpdating}
                              className="inline-flex items-center gap-1 rounded-lg px-2.5 py-1.5 text-xs font-medium text-green-700 bg-green-50 hover:bg-green-100 disabled:opacity-50 transition-colors"
                              title="Mark as Paid"
                            >
                              <CheckCircleIcon className="w-3.5 h-3.5" />
                              Paid
                            </button>
                          )}

                          {/* Add Notes */}
                          <button
                            onClick={() =>
                              setNotesModal({
                                open: true,
                                record: { ...record, tenantName },
                              })
                            }
                            disabled={isUpdating}
                            className="inline-flex items-center gap-1 rounded-lg px-2.5 py-1.5 text-xs font-medium text-gray-600 bg-gray-50 hover:bg-gray-100 disabled:opacity-50 transition-colors"
                            title="Add Notes"
                          >
                            <PencilSquareIcon className="w-3.5 h-3.5" />
                            Notes
                          </button>

                          {/* Loading indicator for this row */}
                          {isUpdating && (
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-purple-600 ml-1" />
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Record count footer */}
        {!loading && records.length > 0 && (
          <div className="px-6 py-3 border-t border-gray-200 bg-gray-50">
            <p className="text-sm text-gray-500">
              Showing {records.length} billing record{records.length !== 1 ? 's' : ''}
              {selectedMonth || selectedTenant ? ' (filtered)' : ''}
            </p>
          </div>
        )}
      </div>

      {/* Notes Modal */}
      <NotesModal
        isOpen={notesModal.open}
        record={notesModal.record}
        onClose={() => setNotesModal({ open: false, record: null })}
        onSave={handleSaveNotes}
      />
    </div>
  );
};

export default SmsBillingPage;
